import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Edit3, Trash2 } from 'lucide-react';
import { Modal, ConfirmationModal } from './Helpers';

const BlockManagementView = ({ showToast, currentUser }) => {
  const [blocks, setBlocks] = useState([]);
  const [blockName, setBlockName] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, blockId: null });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);

  // Blokları yükle (artık bina seçimine gerek yok, currentUser.buildingId kullanılır)
  const loadBlocks = useCallback(async () => {
    if (!currentUser || !currentUser.buildingId) {
      setBlocks([]);
      setIsLoadingBlocks(false);
      return;
    }
    setIsLoadingBlocks(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blocks?buildingId=${currentUser.buildingId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          window.location.reload();
          throw new Error('Oturum süresi doldu veya yetkiniz yok.');
        }
        throw new Error('Bloklar yüklenemedi.');
      }
      const data = await response.json();
      setBlocks(data || []);
    } catch (error) {
      showToast(`Bloklar yüklenirken hata: ${error.message}`, "error");
    } finally {
      setIsLoadingBlocks(false);
    }
  }, [showToast, currentUser]);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  const handleAddOrUpdateBlock = async (e) => {
    e.preventDefault();
    if (!blockName.trim()) {
      showToast('Blok adı boş olamaz.', 'error');
      return;
    }
    if (!currentUser || !currentUser.buildingId) {
      showToast('Kullanıcı bina bilgisi bulunamadı. Lütfen tekrar giriş yapın.', 'error');
      return;
    }

    const blockData = { name: blockName.trim(), buildingId: parseInt(currentUser.buildingId) };
    setIsLoading(true);
    try {
      if (editingBlock) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blocks/${editingBlock.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(blockData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Blok güncellenemedi.');
        }
        showToast('Blok başarıyla güncellendi.', 'success');
      } else {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blocks`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(blockData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Blok eklenemedi.');
        }
        showToast('Blok başarıyla eklendi.', 'success');
      }
      loadBlocks();
      closeBlockModal();
    } catch (error) {
      showToast('Blok kaydedilirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openBlockModalForEdit = (block) => {
    setEditingBlock(block);
    setBlockName(block.name);
    setIsModalOpen(true);
  };

  const openBlockModalForNew = () => {
    setEditingBlock(null);
    setBlockName('');
    setIsModalOpen(true);
  };

  const closeBlockModal = () => {
    setIsModalOpen(false);
    setEditingBlock(null);
    setBlockName('');
  };

  const handleDeleteBlock = async (blockId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blocks/${blockId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Blok silinemedi.');
      }
      showToast('Blok başarıyla silindi.', 'success');
      loadBlocks();
    } catch (error) {
      const errorMessage = error.message || 'Blok silinirken bir hata oluştu.';
      if (error.message && (error.message.includes("ait daireler") || error.message.includes("ilişkili"))) {
        showToast(errorMessage, 'warning', 6000);
      } else {
        showToast(errorMessage, 'error');
      }
    } finally {
      setIsLoading(false);
      setConfirmDeleteModal({ isOpen: false, blockId: null });
    }
  };

  // Tarih formatlama fonksiyonu (Invalid Date sorununu çözmek için)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow relative">
      {isLoading && !isModalOpen && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
          <p className="ml-3 text-teal-700">İşlem yapılıyor...</p>
        </div>
      )}
      <ConfirmationModal
        isOpen={confirmDeleteModal.isOpen}
        onClose={() => setConfirmDeleteModal({ isOpen: false, blockId: null })}
        title="Bloğu Sil"
        message="Bu bloğu silmek istediğinizden emin misiniz? Bloğa ait daireler varsa önce onlar silinmelidir."
        onConfirm={() => handleDeleteBlock(confirmDeleteModal.blockId)}
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Blok Yönetimi {currentUser?.buildingName && `(${currentUser.buildingName})`}</h1>
        <button
          onClick={openBlockModalForNew}
          className="bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 flex items-center disabled:opacity-50"
          disabled={isLoading}
        >
          <PlusCircle size={18} className="mr-2" /> Yeni Blok Ekle
        </button>
      </div>
      {/* Bina filtresi kaldırıldı */}
      <Modal isOpen={isModalOpen} onClose={closeBlockModal} title={editingBlock ? "Bloğu Düzenle" : "Yeni Blok Ekle"}>
        <form onSubmit={handleAddOrUpdateBlock}>
          <p className="mb-2 text-sm text-gray-600"><strong>Bina:</strong> {currentUser?.buildingName || 'Yükleniyor...'}</p>
          <div className="mb-4">
            <label htmlFor="blockNameModal" className="block text-sm font-medium text-gray-700 mb-1">Blok Adı (örn: A Blok):</label>
            <input
              type="text"
              id="blockNameModal"
              value={blockName}
              onChange={(e) => setBlockName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500"
              required
            />
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={closeBlockModal}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              disabled={isLoading}
            >
              İptal
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading && isModalOpen ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white inline-block mr-2"></div>
              ) : null}
              {editingBlock ? "Güncelle" : "Kaydet"}
            </button>
          </div>
        </form>
      </Modal>
      {!currentUser?.buildingId && !isLoading ? <p className="text-gray-500">Blokları görmek için lütfen giriş yapın veya bina atamanızı kontrol edin.</p> : null}
      {currentUser?.buildingId && isLoadingBlocks ?
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
          <p className="ml-3 text-teal-700">Bloklar yükleniyor...</p>
        </div>
      : null}
      {currentUser?.buildingId && !isLoadingBlocks && blocks.length === 0 ? <p className="text-gray-500">Bu binaya ait henüz blok eklenmemiş.</p> : null}
      {currentUser?.buildingId && !isLoadingBlocks && blocks.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="th-style">Blok Adı</th>
                <th className="th-style">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {blocks.map(bl => (
                <tr key={bl.id}>
                  <td className="td-style font-medium">{bl.name}</td>
                  <td className="td-style space-x-2">
                    <button
                      onClick={() => openBlockModalForEdit(bl)}
                      className="icon-button-indigo"
                      title="Düzenle"
                      disabled={isLoading}
                    >
                      <Edit3 size={18}/>
                    </button>
                    <button
                      onClick={() => setConfirmDeleteModal({isOpen: true, blockId: bl.id})}
                      className="icon-button-red"
                      title="Sil"
                      disabled={isLoading}
                    >
                      <Trash2 size={18}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <style jsx>{`
        .th-style { padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; }
        .td-style { padding: 0.75rem 1.5rem; white-space: nowrap; font-size: 0.875rem; color: #374151; }
        .icon-button-indigo { color: #4f46e5; } .icon-button-indigo:hover { color: #3730a3; }
        .icon-button-red { color: #dc2626; } .icon-button-red:hover { color: #b91c1c; }
      `}</style>
    </div>
  );
};

export default BlockManagementView;
